import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Loader2, CheckCircle2, AlertTriangle, Eye, EyeOff, ArrowRight, Building2, User, Mail, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ClientInvitePage({ navigate }) {
  const [token, setToken] = useState('');
  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', password: '', confirmPassword: '', company: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (!t) { setError('No invitation token found'); setLoading(false); return; }
    setToken(t);

    fetch(`${API_URL}/api/invites/verify/${t}`)
      .then(r => r.ok ? r.json() : r.json().then(e => { throw new Error(e.detail || 'Invalid invitation'); }))
      .then(data => {
        setInviteData(data);
        setForm(f => ({ ...f, name: data.name || '' }));
        if (data.alreadyRegistered) {
          setError('You already have an account. Please log in instead.');
        }
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (form.password !== form.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (!acceptedTerms) { toast.error('Please accept the Terms of Service'); return; }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/invites/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          name: form.name,
          password: form.password,
          company: form.company,
          acceptedTerms: true,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        localStorage.setItem('d3_token', data.token);
        localStorage.setItem('d3_user', JSON.stringify(data.user));
        toast.success('Account created! Welcome aboard.');
        window.location.href = '/dashboard';
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Registration failed');
      }
    } catch { toast.error('Connection error'); }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Invitation Issue</h1>
          <p className="text-sm text-zinc-400 mb-6">{error}</p>
          <button onClick={() => navigate('/login')}
            className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-xl transition-colors">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center p-4" data-testid="client-invite-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-600 to-indigo-500 flex items-center justify-center mx-auto mb-4 shadow-[0_0_40px_rgba(79,70,229,0.3)]">
            <Shield className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Join {inviteData?.orgName}</h1>
          <p className="text-sm text-zinc-400">
            {inviteData?.invitedBy ? `${inviteData.invitedBy} invited you to collaborate` : 'You have been invited to join as a client'}
          </p>
          {inviteData?.message && (
            <div className="mt-3 p-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-xs text-zinc-300 italic">
              &ldquo;{inviteData.message}&rdquo;
            </div>
          )}
        </div>

        {/* Registration Form */}
        <form onSubmit={handleSubmit} className="bg-[#111214] border border-white/10 rounded-2xl p-6 space-y-4" data-testid="invite-form">
          <div className="flex items-center gap-3 pb-3 border-b border-white/5 mb-2">
            <Building2 className="w-4 h-4 text-indigo-400" />
            <span className="text-sm text-zinc-300">{inviteData?.orgName}</span>
            <span className="ml-auto px-2 py-0.5 text-[10px] bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded-full">Client</span>
          </div>

          <div>
            <label className="block text-xs text-zinc-400 mb-1.5">Full Name *</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full pl-10 pr-4 py-2.5 text-sm bg-white/5 border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40"
                placeholder="Your full name" required data-testid="invite-name" />
            </div>
          </div>

          <div>
            <label className="block text-xs text-zinc-400 mb-1.5">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
              <input value={inviteData?.email || ''} disabled
                className="w-full pl-10 pr-4 py-2.5 text-sm bg-white/5 border border-white/10 rounded-xl text-zinc-500 cursor-not-allowed" />
            </div>
          </div>

          <div>
            <label className="block text-xs text-zinc-400 mb-1.5">Company (Optional)</label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
              <input value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                className="w-full pl-10 pr-4 py-2.5 text-sm bg-white/5 border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40"
                placeholder="Your company name" data-testid="invite-company" />
            </div>
          </div>

          <div>
            <label className="block text-xs text-zinc-400 mb-1.5">Password *</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
              <input type={showPassword ? 'text' : 'password'} value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                className="w-full pl-10 pr-10 py-2.5 text-sm bg-white/5 border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40"
                placeholder="At least 8 characters" required data-testid="invite-password" />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs text-zinc-400 mb-1.5">Confirm Password *</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
              <input type="password" value={form.confirmPassword}
                onChange={e => setForm(f => ({ ...f, confirmPassword: e.target.value }))}
                className="w-full pl-10 pr-4 py-2.5 text-sm bg-white/5 border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40"
                placeholder="Confirm your password" required data-testid="invite-confirm-password" />
            </div>
          </div>

          <label className="flex items-start gap-2 cursor-pointer py-1">
            <input type="checkbox" checked={acceptedTerms} onChange={e => setAcceptedTerms(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/5 text-indigo-600 focus:ring-indigo-500/30"
              data-testid="accept-terms" />
            <span className="text-xs text-zinc-400">
              I agree to the <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:underline">Terms of Service</a> and <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:underline">Privacy Policy</a>
            </span>
          </label>

          <button type="submit" disabled={submitting || !form.name || !form.password || !form.confirmPassword || !acceptedTerms}
            className="w-full py-3 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
            data-testid="submit-invite-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> Create Account & Join</>}
          </button>

          <p className="text-center text-[11px] text-zinc-600">
            Already have an account? <button type="button" onClick={() => navigate('/login')} className="text-indigo-400 hover:underline">Log in</button>
          </p>
        </form>
      </motion.div>
    </div>
  );
}
